import React, {ReactElement, useState, useMemo, useEffect} from 'react';
import ArrSelectableColumnChart, {
  SelectedBarInfoType,
  TimelineType,
} from '../../../../../common/arr-selectable-column-chart/ArrSelectableColumnChart';
import {convertApiToChart} from '../../../../../common/arr-selectable-column-chart/chartUtils';
import {CustomMenuItem} from '../../../../../common/arr-menu/ArrMenu';
import {
  ReportActionMenuOptionTypes,
  SourceTypeCode,
  TIMELINE_COLOR_MAP,
} from '../../../../../../shared/constants';
import './TimelineCharts.scss';
import {t} from 'i18next';
import ActionMenuWrapper from '../../../../../action-menu/action-menu-wrapper/ActionMenuWrapper';

type TimelineChartsProps = {
  data?: {
    yearlyData: any[];
    monthlyData: any[];
    dailyData: any[];
  };
  loading?: boolean;
  roleId: number | undefined;
  onActionPick?: (
    item: CustomMenuItem,
    selectedBars: Map<string, SelectedBarInfoType>,
    timelineType: TimelineType
  ) => void;
  actionMenuAditioanlProp: {
    sourceDetails?: {[key: string]: any};
    sourceType: SourceTypeCode;
    sourceTypeContext?: string;
  };
  getSourceDetails?: (selectedBar: Map<string, SelectedBarInfoType>) => {
    [key: string]: any;
  };
  getDoActionData?: (data: {
    operationType?: string;
    sourceDetails?: {[key: string]: any};
    likeActionList?: any[];
  }) => {selectedList?: object; destinationList?: object};
  getUpdatedSourceDetails: (
    sourceDetails: {[key: string]: any},
    forAPI?: string
  ) => {
    [key: string]: any;
  };
  onUpdateSelectedBar?: (selectedBar: Map<string, SelectedBarInfoType>) => void;
};
const actionStringPath = 'reports.actionMenu.';
const timelineStringPath = 'reports.timelineAndGaps.';

const TimelineCharts: React.FC<
  TimelineChartsProps & {timelineType?: string}
> = ({
  data,
  loading,
  timelineType,
  onActionPick,
  roleId,
  actionMenuAditioanlProp,
  getSourceDetails,
  getDoActionData,
  getUpdatedSourceDetails,
  onUpdateSelectedBar,
}) => {
  const [selectedBar, setSelectedBar] = useState<
    Map<string, SelectedBarInfoType>
  >(new Map());

  useEffect(() => {
    if (onUpdateSelectedBar) {
      onUpdateSelectedBar(selectedBar);
    }
  }, [selectedBar]);

  const getYearlyDuration = (yearlyData: any[]) => {
    if (!yearlyData || yearlyData.length === 0) return '';
    const startYear = yearlyData[0].periodValue.split('/')[2];
    const endYear = yearlyData[yearlyData.length - 1].periodValue.split('/')[2];
    return `${startYear}-${endYear}`;
  };
  const yearlyDuration = getYearlyDuration(data?.yearlyData || []);

  const monthYearLabel = useMemo(() => {
    if (!data?.dailyData || data.dailyData.length === 0) return [];
    const monthMap = new Map<string, string>();
    data.dailyData.forEach((item) => {
      const date = new Date(item.period);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      if (!monthMap.has(key)) {
        monthMap.set(
          key,
          date.toLocaleString('default', {month: 'long', year: 'numeric'})
        );
      }
    });
    return Array.from(monthMap.values());
  }, [data]);

  const {labels: yearlyLabels, series: yearlySeries} = convertApiToChart(
    data?.yearlyData || [],
    'yearly'
  );
  const {labels: monthlyLabels, series: monthlySeries} = convertApiToChart(
    data?.monthlyData || [],
    'monthly'
  );
  const {labels: dailyLabels, series: dailySeries} = convertApiToChart(
    data?.dailyData || [],
    'daily'
  );

  const getActionMenuItems = (timelineType: TimelineType): CustomMenuItem[] => {
    const disableOption = selectedBar.size === 0;

    const bulkActionItem: CustomMenuItem | null =
      roleId === 1
        ? {
            id: ReportActionMenuOptionTypes.BulkRelease,
            label: t(actionStringPath + 'bulkRelease'),
            disabled: disableOption,
          }
        : roleId === 2
          ? {
              id: ReportActionMenuOptionTypes.BulkAssign,
              label: t(actionStringPath + 'bulkAssign'),
              disabled: disableOption,
            }
          : null;

    const baseItems: CustomMenuItem[] = [
      {
        id: ReportActionMenuOptionTypes.View,
        label: t(actionStringPath + 'view'),
        disabled: disableOption,
        submenu: [
          {
            id: ReportActionMenuOptionTypes.ViewInDocView,
            label: t(actionStringPath + 'viewInDocView'),
          },
          {
            id: ReportActionMenuOptionTypes.ViewInDocViewNewTab,
            label: t(actionStringPath + 'viewInDocViewNewTab'),
          },
          {
            id: ReportActionMenuOptionTypes.ViewInDocList,
            label: t(actionStringPath + 'viewInDocList'),
          },
        ],
      },
      {
        id: ReportActionMenuOptionTypes.BulkTag,
        label: t(actionStringPath + 'bulkTag'),
        disabled: disableOption,
      },
      {
        id: ReportActionMenuOptionTypes.BulkFolder,
        label: t(actionStringPath + 'bulkFolder'),
        disabled: disableOption,
      },
      ...(bulkActionItem ? [bulkActionItem] : []),
      {
        id: ReportActionMenuOptionTypes.Tally,
        label: t(actionStringPath + 'tally'),
        disabled: disableOption,
      },
      {
        id: ReportActionMenuOptionTypes.ExportData,
        label: t(actionStringPath + 'exportData'),
        disabled: disableOption,
      },
    ];

    if (timelineType === 'yearly') {
      baseItems.unshift({
        id: ReportActionMenuOptionTypes.Monthly,
        label: t(actionStringPath + 'monthlyTimeline'),
        disabled: disableOption,
      });
    }

    if (timelineType === 'monthly') {
      baseItems.unshift({
        id: ReportActionMenuOptionTypes.Daily,
        label: t(actionStringPath + 'dailyTimeline'),
        disabled: disableOption,
      });
    }

    if (timelineType === 'yearly' || timelineType === 'monthly') {
      baseItems.splice(1, 0, {
        id: ReportActionMenuOptionTypes.GenerateGap,
        label: t(actionStringPath + 'generateGaps'),
      });
    }

    return baseItems;
  };

  const handleTimelineSelect = (
    info: SelectedBarInfoType,
    isUnselected?: boolean
  ) => {
    const updatedSelectedBar = new Map(selectedBar);
    if (isUnselected) {
      updatedSelectedBar.delete(info.barKey);
    } else {
      updatedSelectedBar.set(info.barKey, info);
    }
    setSelectedBar(updatedSelectedBar);
  };

  const handleActionPick =
    (timelineType: TimelineType) => (item: CustomMenuItem) => {
      onActionPick?.(item, selectedBar, timelineType);
    };

  const getReactionActionButton = (timeline: TimelineType): ReactElement => {
    return (
      <ActionMenuWrapper
        size="small"
        actions={getActionMenuItems(timeline.toLowerCase() as TimelineType)}
        dataTestId={`timeline-action-menu-${timeline.toLowerCase()}`}
        onActionPick={handleActionPick(timeline.toLowerCase() as TimelineType)}
        {...actionMenuAditioanlProp}
        sourceDetails={
          getSourceDetails ? (getSourceDetails(selectedBar) ?? {}) : {}
        }
        getDoActionData={getDoActionData}
        getSourceDetails={getUpdatedSourceDetails}
      />
    );
  };

  return (
    <div className="tl-charts" data-testid={`timeline-charts-loading-div`}>
      {loading && (
        <div className="tl-loading" data-testid={`timeline-charts-loading`}>
          {t(timelineStringPath + 'loadingState')}
        </div>
      )}
      {!loading && !data && (
        <div className="tl-empty" data-testid={`timeline-charts-no-loading`}>
          {t(timelineStringPath + 'noDataState')}
        </div>
      )}
      {!loading &&
        data &&
        timelineType === 'YEARLY' &&
        data.yearlyData?.length > 0 && (
          <ArrSelectableColumnChart
            labels={yearlyLabels}
            series={yearlySeries}
            barColor={TIMELINE_COLOR_MAP.DefaultSeries}
            barActiveColor={TIMELINE_COLOR_MAP.Yearly}
            title={`${t(timelineStringPath + 'yearlyTimelineHeading')} ${yearlyDuration}`}
            timelineType="yearly"
            onTimelineSelect={handleTimelineSelect}
            actionMenuItems={getActionMenuItems('yearly')}
            onActionPick={handleActionPick('yearly')}
            selectedBars={Array.from(selectedBar.keys())}
            rightActionComponent={getReactionActionButton('yearly')}
            dataTestId={'yearly-column-chart'}
          />
        )}
      {!loading &&
        data &&
        timelineType === 'MONTHLY' &&
        data.monthlyData?.length > 0 && (
          <ArrSelectableColumnChart
            labels={monthlyLabels}
            series={monthlySeries}
            barColor={TIMELINE_COLOR_MAP.DefaultSeries}
            barActiveColor={TIMELINE_COLOR_MAP.Monthly}
            title={t(timelineStringPath + 'monthlyTimelineHeading')}
            timelineType="monthly"
            onTimelineSelect={handleTimelineSelect}
            actionMenuItems={getActionMenuItems('monthly')}
            onActionPick={handleActionPick('monthly')}
            selectedBars={Array.from(selectedBar.keys())}
            rightActionComponent={getReactionActionButton('monthly')}
            dataTestId={'monthly-column-chart'}
          />
        )}
      {!loading &&
        data &&
        timelineType === 'DAILY' &&
        data.dailyData?.length > 0 && (
          <ArrSelectableColumnChart
            labels={dailyLabels}
            series={dailySeries}
            barColor={TIMELINE_COLOR_MAP.DefaultSeries}
            barActiveColor={TIMELINE_COLOR_MAP.Daily}
            title={t(timelineStringPath + 'dailyTimelineHeading')}
            timelineType="daily"
            dataTestId={'daily-column-chart'}
            footerCaption={
              <div
                className="daily-footer-labels"
                data-testid={`timeline-charts-div-icon`}
              >
                {monthYearLabel.map((label, idx) => (
                  <span
                    key={idx}
                    className="footer-label"
                    data-testid={`timeline-charts-span`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            }
            onTimelineSelect={handleTimelineSelect}
            actionMenuItems={getActionMenuItems('daily')}
            onActionPick={handleActionPick('daily')}
            selectedBars={Array.from(selectedBar.keys())}
            rightActionComponent={getReactionActionButton('daily')}
          />
        )}
    </div>
  );
};

export default TimelineCharts;
